// VerificationStatusSection.jsx
import React, { useState, useEffect, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../../config/firebase-config';
import { sendEmailVerification } from 'firebase/auth';
import { 
  CheckCircle, 
  AlertCircle, 
  Mail, 
  Phone, 
  Shield, 
  ArrowRight,
  RefreshCw,
  Lock,
  Unlock,
  Crown,
  Sparkles,
  TrendingUp,
  Zap,
  RotateCcw
} from 'lucide-react';

const VerificationStatusSection = ({ user, userProfile, verificationStatus: propVerificationStatus, phoneNumber, onVerificationUpdate }) => {
  const [verificationStatus, setVerificationStatus] = useState(propVerificationStatus || {
    emailVerified: false,
    phoneVerified: false,
    onboardingCompleted: false,
    canReceiveRequests: false
  });
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [isResetting, setIsResetting] = useState({ email: false, phone: false });
  const [isTestMode, setIsTestMode] = useState(false);

  const functions = getFunctions();

  // Update local state when props change
  useEffect(() => {
    if (propVerificationStatus) {
      setVerificationStatus(propVerificationStatus);
    }
  }, [propVerificationStatus]);
  
  // Auto-refresh verification status every 30 seconds if not verified
  useEffect(() => {
    if (!verificationStatus.emailVerified || !verificationStatus.phoneVerified) {
      const interval = setInterval(() => {
        checkVerificationStatus();
      }, 30000); // 30 seconds
      
      return () => clearInterval(interval);
    }
  }, [verificationStatus.emailVerified, verificationStatus.phoneVerified]);

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  const checkVerificationStatus = async () => {
    if (!user) return;
    
    setIsRefreshing(true);
    try {
      // First, reload the user to get fresh Firebase Auth status
      await user.reload();
      
      // Get the current Firestore status first
      const getUserVerificationStatus = httpsCallable(functions, 'getUserVerificationStatus');
      const result = await getUserVerificationStatus();
      
      if (result.data.success) {
        console.log('🔍 Verification status:', result.data.verificationStatus);
        setVerificationStatus(result.data.verificationStatus);
        if (onVerificationUpdate) {
          onVerificationUpdate(result.data.verificationStatus);
        }
      }
    } catch (error) {
      console.error('Failed to fetch verification status:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getVerificationLevel = () => {
    const emailVerified = verificationStatus.emailVerified;
    const phoneVerified = verificationStatus.phoneVerified;
    const onboardingCompleted = verificationStatus.onboardingCompleted;
    
    if (emailVerified && phoneVerified && onboardingCompleted) {
      return {
        level: 'premium',
        title: '👑 Premium Account',
        description: 'Full verification complete - maximum earning potential!',
        color: '#7C3AED',
        bgColor: '#F3F4F6',
        icon: Crown
      };
    } else if (emailVerified && onboardingCompleted) {
      return {
        level: 'verified',
        title: '✅ Verified Account',
        description: 'Ready to earn! Add phone verification for instant alerts.',
        color: '#10B981',
        bgColor: '#ECFDF5',
        icon: Unlock
      };
    } else if (onboardingCompleted) {
      return {
        level: 'basic',
        title: '⭐ Basic Account',
        description: 'Complete verification to unlock full earning potential.',
        color: '#F59E0B',
        bgColor: '#FFFBEB',
        icon: Lock
      };
    } else {
      return {
        level: 'new',
        title: '🚀 Getting Started',
        description: 'Complete your setup to start earning from your music!',
        color: '#6B7280',
        bgColor: '#F9FAFB',
        icon: Sparkles
      };
    }
  };

  const verificationLevel = getVerificationLevel();
  const StatusIcon = verificationLevel.icon;

  return (
    <div style={{
      backgroundColor: '#1F2937',
      border: '4px solid #7C3AED',
      borderRadius: '16px',
      overflow: 'hidden',
      marginBottom: '24px'
    }}>
      {/* Header */}
      <div style={{
        padding: '24px',
        borderBottom: '4px solid #6D28D9',
        backgroundColor: '#581C87'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: 'white',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Shield size={24} />
            Account Verification
          </h3>
          
          <button
            onClick={checkVerificationStatus}
            disabled={isRefreshing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '8px 12px',
              color: 'white',
              fontSize: '14px',
              cursor: isRefreshing ? 'not-allowed' : 'pointer',
              opacity: isRefreshing ? 0.7 : 1
            }}
          >
            <RefreshCw 
              size={14} 
              style={{ 
                animation: isRefreshing ? 'spin 1s linear infinite' : 'none' 
              }} 
            />
            Refresh
          </button>
        </div>
        
        <p style={{
          color: '#C4B5FD',
          margin: 0,
          fontSize: '14px'
        }}>
          Increase your earning potential with verified credentials
        </p>
      </div>

      <div style={{ padding: '24px' }}>
        {/* Current Status */}
        <div style={{
          backgroundColor: verificationLevel.bgColor,
          border: `2px solid ${verificationLevel.color}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              backgroundColor: verificationLevel.color,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <StatusIcon size={24} style={{ color: 'white' }} />
            </div>
            <div>
              <h4 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                margin: '0 0 4px 0',
                color: verificationLevel.color
              }}>
                {verificationLevel.title}
              </h4>
              <p style={{
                fontSize: '14px',
                margin: 0,
                color: '#374151'
              }}>
                {verificationLevel.description}
              </p>
            </div>
          </div>
        </div>

        {/* Verification Steps */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          {/* Email Verification */}
          <div style={{
            backgroundColor: '#374151',
            borderRadius: '12px',
            padding: '16px',
            border: verificationStatus.emailVerified 
              ? '2px solid #10B981' 
              : '2px solid #F59E0B'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                backgroundColor: verificationStatus.emailVerified ? '#10B981' : '#F59E0B',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Mail size={16} style={{ color: 'white' }} />
              </div>
              <div>
                <h5 style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  margin: '0 0 2px 0',
                  color: 'white'
                }}>
                  Email Verification
                </h5>
                <p style={{
                  fontSize: '12px',
                  margin: 0,
                  color: verificationStatus.emailVerified ? '#10B981' : '#F59E0B'
                }}>
                  {verificationStatus.emailVerified ? '✅ Verified' : '⚠️ Not Verified'}
                </p>
              </div>
            </div>
            
            {!verificationStatus.emailVerified ? (
              <>
              <button
                style={{
                  width: '100%',
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
                onClick={async () => {
                  try {
                    const currentUser = auth.currentUser;
                    
                    if (!currentUser) {
                      alert('Please sign in to verify your email.');
                      return;
                    }
                    
                    console.log('📧 Sending verification email using Firebase Auth...');
                    
                    // Use clean SMTP version (no earnings text)
                    try {
                      const sendEmailVerification = httpsCallable(functions, 'sendEmailVerification');
                      const result = await sendEmailVerification();
                      
                      if (!result.data.success) {
                        throw new Error(result.data.message);
                      }
                      
                      alert(
                        '✅ Email verification sent!\n\n' +
                        '📧 Please check your inbox for the verification link.\n\n' +
                        '💡 Important:\n' +
                        '• Check spam/junk folder\n' +
                        '• Email from: vouchers@requestasong.com.au\n' +
                        '• May take a few minutes to arrive\n' +
                        '• After clicking link, refresh this page'
                      );
                      
                      console.log('Email verification sent successfully to:', currentUser.email);
                      
                      // Update Firestore to track the request (now using clean SMTP version)
                      const checkStatusFunc = httpsCallable(functions, 'checkEmailVerification');
                      await checkStatusFunc();
                      
                    } catch (firebaseError) {
                      console.error('Firebase email error:', firebaseError);
                      
                      // If already verified, update the status
                      if (firebaseError.code === 'auth/email-already-verified') {
                        alert('Your email is already verified! ✅');
                        checkVerificationStatus();
                      } else if (firebaseError.code === 'auth/too-many-requests') {
                        alert('Too many requests. Please wait a few minutes before trying again.');
                      } else if (firebaseError.code === 'auth/missing-email') {
                        alert('No email address associated with this account.');
                      } else if (firebaseError.code === 'auth/user-disabled') {
                        alert('This account has been disabled.');
                      } else {
                        alert(`Firebase Error: ${firebaseError.message}\n\nCode: ${firebaseError.code}`);
                        throw firebaseError;
                      }
                    }
                  } catch (error) {
                    console.error('Email verification error:', error);
                    
                    if (error.code === 'functions/resource-exhausted') {
                      alert('Too many requests. Please wait a few minutes before trying again.');
                    } else if (error.code === 'functions/unauthenticated') {
                      alert('Please sign in to verify your email.');
                    } else {
                      alert('Failed to send verification email: ' + error.message);
                    }
                  }
                }}
              >
                Verify Email
              </button>
              {/* ✅ QUICK FIX BUTTON */}
              <button
                style={{
                  marginLeft: '10px',
                  padding: '8px 16px',
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
                onClick={async () => {
                  try {
                    console.log('🔧 Manually fixing email verification...');
                    const fixEmailVerification = httpsCallable(functions, 'fixEmailVerification');
                    const result = await fixEmailVerification();
                    console.log('Fix result:', result.data);
                    alert('✅ Email verification fixed! Refreshing page...');
                    window.location.reload();
                  } catch (error) {
                    console.error('Fix error:', error);
                    alert('Fix failed: ' + error.message);
                  }
                }}
              >
                Quick Fix Email
              </button>
              {isTestMode && (
                <button
                  style={{
                    width: '100%',
                    backgroundColor: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    marginTop: '8px'
                  }}
                  onClick={async () => {
                    try {
                      const simulateFunc = httpsCallable(functions, 'simulateEmailVerification');
                      const result = await simulateFunc();
                      
                      if (result.data.success) {
                        alert(result.data.message);
                        setIsTestMode(false);
                        checkVerificationStatus();
                      }
                    } catch (error) {
                      console.error('Simulation error:', error);
                      alert('Failed to simulate verification: ' + error.message);
                    }
                  }}
                >
                  Simulate Verification (Test Mode)
                </button>
              )}
              </>
            ) : (
              <button
                style={{
                  width: '100%',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: isResetting.email ? 'not-allowed' : 'pointer',
                  opacity: isResetting.email ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onClick={async () => {
                  if (isResetting.email) return;
                  
                  const confirm = window.confirm(
                    'This will reset your email verification status. You\'ll need to verify your email again.\n\n' +
                    'Use this if you\'ve changed your email address.\n\n' +
                    'Continue?'
                  );
                  
                  if (!confirm) return;
                  
                  setIsResetting({ ...isResetting, email: true });
                  try {
                    const resetVerificationStatus = httpsCallable(functions, 'resetVerificationStatus');
                    const result = await resetVerificationStatus({ resetType: 'email' });
                    
                    if (result.data.success) {
                      alert(result.data.message);
                      checkVerificationStatus();
                    }
                  } catch (error) {
                    console.error('Reset email verification error:', error);
                    alert('Failed to reset email verification: ' + error.message);
                  } finally {
                    setIsResetting({ ...isResetting, email: false });
                  }
                }}
                disabled={isResetting.email}
              >
                <RotateCcw size={14} />
                Reset Verification
              </button>
            )}
          </div>

          {/* Phone Verification */}
          <div style={{
            backgroundColor: '#374151',
            borderRadius: '12px',
            padding: '16px',
            border: verificationStatus.phoneVerified 
              ? '2px solid #10B981' 
              : '2px solid #7C3AED'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '12px'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                backgroundColor: verificationStatus.phoneVerified ? '#10B981' : '#7C3AED',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Phone size={16} style={{ color: 'white' }} />
              </div>
              <div>
                <h5 style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  margin: '0 0 2px 0',
                  color: 'white'
                }}>
                  Phone Verification
                </h5>
                <p style={{
                  fontSize: '12px',
                  margin: 0,
                  color: verificationStatus.phoneVerified ? '#10B981' : '#A855F7'
                }}>
                  {verificationStatus.phoneVerified ? '✅ Verified' : '⭐ Recommended'}
                </p>
              </div>
            </div>
            
            {!verificationStatus.phoneVerified ? (
              <>
              <button
                onClick={async () => {
                  const existingPhone = phoneNumber || verificationStatus.phoneNumber || userProfile?.phoneNumber || '';
                  
                  if (!existingPhone) {
                    alert('Please add a phone number to your profile first by going to the Profile tab.');
                    return;
                  }
                  
                  // Rate limiting check
                  const lastSentKey = `last_sms_sent_${user.uid}`;
                  const countKey = `sms_count_${user.uid}_${new Date().toDateString()}`;
                  
                  const lastSent = localStorage.getItem(lastSentKey);
                  const todayCount = parseInt(localStorage.getItem(countKey) || '0');
                  const now = Date.now();
                  
                  // Check if user is trying to send too soon (1 minute cooldown)
                  if (lastSent && (now - parseInt(lastSent)) < 60000) {
                    const remainingSeconds = Math.ceil((60000 - (now - parseInt(lastSent))) / 1000);
                    setCooldownSeconds(remainingSeconds);
                    alert(`Please wait ${remainingSeconds} seconds before requesting another code.`);
                    return;
                  }
                  
                  // Check daily limit (5 attempts per day)
                  if (todayCount >= 5) {
                    alert('You have reached the daily limit of 5 verification attempts. Please try again tomorrow.');
                    return;
                  }
                  
                  console.log('📱 Sending verification code to:', existingPhone);
                  console.log('🔍 DEBUG - About to call HTTP function sendPhoneVerificationHttp');
                  
                  try {
                    // Update rate limiting
                    localStorage.setItem(lastSentKey, now.toString());
                    localStorage.setItem(countKey, (todayCount + 1).toString());
                    
                    // Use the HTTP version with CORS support  
                    console.log('🔍 DEBUG - Making HTTP request to sendPhoneVerificationHttp');
                    const response = await fetch('https://us-central1-requestasong-4472b.cloudfunctions.net/sendPhoneVerificationHttp', {
                      method: 'POST',
                      headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${await user.getIdToken()}`
                      },
                      body: JSON.stringify({ phoneNumber: existingPhone })
                    });

                    console.log('🔍 DEBUG - HTTP response status:', response.status);
                    
                    if (!response.ok) {
                      throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const smsResult = await response.json();
                    console.log('🔍 DEBUG - HTTP response data:', smsResult);

                    if (smsResult && smsResult.success) {
                      // The verification function generates its own code, so we need to use verifyPhoneCode
                      const code = prompt(`We sent a verification code to ${existingPhone}.\n\nEnter the 6-digit code:`);
                      
                      if (code && code.trim()) {
                        console.log('📱 Verifying code:', code.trim());
                        
                        // Use the proper verifyPhoneCode function
                        try {
                          const verifyPhoneCodeFunc = httpsCallable(functions, 'verifyPhoneCode');
                          const verifyResult = await verifyPhoneCodeFunc({ 
                            code: code.trim() 
                          });

                          if (verifyResult.data && verifyResult.data.success) {
                            alert('🎉 Phone verified successfully! You\'ll now receive instant SMS notifications.');
                            
                            // Update verification status
                            setVerificationStatus(prev => ({
                              ...prev,
                              phoneVerified: true,
                              phoneNumber: existingPhone
                            }));
                            
                            // Refresh status
                            checkVerificationStatus();
                          } else {
                            alert('Invalid verification code. Please try again.');
                          }
                        } catch (verifyError) {
                          console.error('Verify code error:', verifyError);
                          alert('Error verifying code: ' + verifyError.message);
                        }
                      }
                    } else {
                      throw new Error(smsResult?.message || 'Failed to send verification code');
                    }
                  } catch (error) {
                    console.error('Phone verification error:', error);
                    alert('Error: ' + (error.message || 'Failed to send verification code. Please try again.'));
                  }
                }}
                style={{
                  width: '100%',
                  backgroundColor: cooldownSeconds > 0 ? '#6B7280' : '#7C3AED',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: cooldownSeconds > 0 ? 'not-allowed' : 'pointer',
                  opacity: cooldownSeconds > 0 ? 0.7 : 1
                }}
                disabled={cooldownSeconds > 0}
              >
                {cooldownSeconds > 0 
                  ? `Wait ${cooldownSeconds}s` 
                  : `Verify Phone (${phoneNumber || verificationStatus.phoneNumber || 'Add phone first'})`
                }
              </button>
              <button
                style={{
                  width: '100%',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: isResetting.phone ? 'not-allowed' : 'pointer',
                  opacity: isResetting.phone ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onClick={async () => {
                  if (isResetting.phone) return;
                  
                  const confirm = window.confirm(
                    'This will reset your phone verification status. You\'ll need to verify your phone again.\n\n' +
                    'Use this if you\'ve changed your phone number.\n\n' +
                    'Continue?'
                  );
                  
                  if (!confirm) return;
                  
                  setIsResetting({ ...isResetting, phone: true });
                  try {
                    const resetVerificationStatus = httpsCallable(functions, 'resetVerificationStatus');
                    const result = await resetVerificationStatus({ resetType: 'phone' });
                    
                    if (result.data.success) {
                      alert(result.data.message);
                      checkVerificationStatus();
                    }
                  } catch (error) {
                    console.error('Reset phone verification error:', error);
                    alert('Failed to reset phone verification: ' + error.message);
                  } finally {
                    setIsResetting({ ...isResetting, phone: false });
                  }
                }}
                disabled={isResetting.phone}
              >
                <RotateCcw size={14} />
                Reset Verification
              </button>
              </>
            ) : (
              <button
                style={{
                  width: '100%',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: isResetting.phone ? 'not-allowed' : 'pointer',
                  opacity: isResetting.phone ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
                onClick={async () => {
                  if (isResetting.phone) return;
                  
                  const confirm = window.confirm(
                    'This will reset your phone verification status. You\'ll need to verify your phone again.\n\n' +
                    'Use this if you\'ve changed your phone number.\n\n' +
                    'Continue?'
                  );
                  
                  if (!confirm) return;
                  
                  setIsResetting({ ...isResetting, phone: true });
                  try {
                    const resetVerificationStatus = httpsCallable(functions, 'resetVerificationStatus');
                    const result = await resetVerificationStatus({ resetType: 'phone' });
                    
                    if (result.data.success) {
                      alert(result.data.message);
                      checkVerificationStatus();
                    }
                  } catch (error) {
                    console.error('Reset phone verification error:', error);
                    alert('Failed to reset phone verification: ' + error.message);
                  } finally {
                    setIsResetting({ ...isResetting, phone: false });
                  }
                }}
                disabled={isResetting.phone}
              >
                <RotateCcw size={14} />
                Reset Verification
              </button>
            )}
          </div>
        </div>

        {/* Benefits Section */}
        <div style={{
          backgroundColor: '#EFF6FF',
          borderRadius: '12px',
          padding: '16px',
          border: '1px solid #DBEAFE'
        }}>
          <h5 style={{
            fontSize: '14px',
            fontWeight: 'bold',
            margin: '0 0 12px 0',
            color: '#1E40AF',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <TrendingUp size={16} />
            Verification Benefits
          </h5>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            fontSize: '12px',
            color: '#1E40AF'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Zap size={12} style={{ color: '#10B981' }} />
              <span>Instant SMS notifications</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <TrendingUp size={12} style={{ color: '#10B981' }} />
              <span>40% higher earnings</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Crown size={12} style={{ color: '#10B981' }} />
              <span>Premium features</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={12} style={{ color: '#10B981' }} />
              <span>Enhanced trust</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default VerificationStatusSection;